import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, Play, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { useTriggerAutoRebook } from '@/lib/disruptions/use-disruptions';
import type { BulkAcceptPreview } from '@/lib/disruptions/api';
import type { ResolutionState } from '@/lib/disruptions/types';
import { formatPriceMinor } from '../labels';
import { rebookingPolicyGate } from '../resolution-steps';
import { factsOf } from './tower-model';
import { scheduleCatchUp, useTowerJournal } from './tower-journal';

// "Rebook again with other parameters" — the operator's way to REDO the
// reversible step: quality rank (#1/#2/#3), a per-group PRICE cap (P7: caps mean the
// price the airline pays — sent as `priceCapUsd`), whether the best
// option is accepted automatically, and `regenerate` (fresh options even where
// some exist). Same seam as the classic sidebar (`auto-rebook` v2). The domain
// preserves committed groups: ticketed groups never move; a SELECTED group keeps
// its selection unless it is cancelled / overridden per PNR — the form says so.

interface RebookAgainProps {
  disruptionUrn: string;
  state: ResolutionState;
  /** A run (or another fire) is in flight — offer nothing until it settles. */
  disabled?: boolean;
}

export function RebookAgain({ disruptionUrn, state, disabled = false }: RebookAgainProps) {
  const { t } = useTranslation('tower');
  const [open, setOpen] = useState(false);
  const [quality, setQuality] = useState<'1' | '2' | '3'>('1');
  const [cap, setCap] = useState('');
  const [acceptTop, setAcceptTop] = useState(true);
  const [regenerate, setRegenerate] = useState(true);
  const [preview, setPreview] = useState<BulkAcceptPreview | null>(null);
  const trigger = useTriggerAutoRebook(disruptionUrn);
  const qc = useQueryClient();
  const append = useTowerJournal((s) => s.append);
  const setPending = useTowerJournal((s) => s.setPending);
  const g = factsOf(state);
  const openGroups = g ? Math.max(0, g.rebookable - g.assigned) : 0;
  const pending = useTowerJournal((s) => s.pending[disruptionUrn]);
  // Once something is COMMITTED (a ticket issued, an offer accepted) or the
  // disruption is contained, "rebook again" makes no sense — every group either
  // flies its ticketed flight or waits for its offer; regenerating would move
  // nothing. Stay mounted while our own fire is pending (its callbacks live here).
  // The pre-run policy gate hides it outright: when the station's policy
  // disables the engine (rebooking TDS §18) a run could only end
  // POLICY_DISABLED, so the action is never offered — the console shows the rule
  // instead (RebookingPolicyNotice).
  const rebookingDone = state.steps.find((st) => st.op === 'rebooking')?.status === 'DONE';
  const policyDisabled = rebookingPolicyGate(state.steps).disabled;
  const contained = state.steps.find((st) => st.op === 'contain')?.status === 'DONE';
  const committed = !!g && (g.ticketed > 0 || g.accepted > 0 || (g.issued ?? 0) > 0);
  const hidden =
    policyDisabled || (!pending && (contained || committed || (openGroups === 0 && rebookingDone)));
  const capUsd = cap.trim() === '' ? undefined : Number(cap);
  const capValid = capUsd === undefined || (Number.isFinite(capUsd) && capUsd >= 0);

  const args = () => ({
    acceptTopRank: acceptTop,
    qualityRank: Number(quality) as 1 | 2 | 3,
    ...(capUsd !== undefined && Number.isFinite(capUsd) ? { priceCapUsd: capUsd } : {}),
    ...(regenerate ? { regenerate: true } : {}),
  });

  const simulate = (): void => {
    trigger.mutate(
      { ...args(), dryRun: true },
      { onSuccess: (res) => setPreview(res.preview ?? null) },
    );
  };
  const run = (): void => {
    const total = g?.rebookable ?? 0;
    setPending(disruptionUrn, { op: 'rebooking', since: Date.now(), count: openGroups || total });
    append(
      disruptionUrn,
      t('rebookAgain.log.sent', {
        rank: quality,
        cap: capUsd !== undefined ? t('rebookAgain.log.cap', { amount: capUsd }) : '',
        accept: acceptTop
          ? t('rebookAgain.log.acceptBest')
          : t('rebookAgain.log.optionsOnly'),
        regen: regenerate ? t('rebookAgain.log.regenerate') : '',
      }),
      'info',
    );
    trigger.mutate(args(), {
      onSuccess: (res) => {
        append(
          disruptionUrn,
          t('rebookAgain.log.queued', { groups: res.queuedGroups, cases: res.queuedCases }),
          'info',
        );
        setPreview(null);
        setOpen(false);
        void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'resolution-state'] });
        void qc.invalidateQueries({ queryKey: ['disruptions', disruptionUrn, 'progress'] });
      },
      onError: (err) =>
        append(disruptionUrn, t('rebookAgain.log.failed', { message: err.message }), 'danger'),
      onSettled: () => {
        setPending(disruptionUrn, null);
        scheduleCatchUp(qc, disruptionUrn);
      },
    });
  };

  if (hidden) return null;
  return (
    <div className="grid gap-1.5" data-testid="tower-rebook-again">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 self-start text-[11px] font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? t('rebookAgain.disabledTitle') : undefined}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden />
        )}
        <RotateCcw className="h-3 w-3" aria-hidden /> {t('rebookAgain.toggle')}
      </button>
      {open && !disabled ? (
        <div className="grid gap-2 rounded-md border border-border bg-muted/30 p-2.5 text-xs">
          <p className="text-[11px] text-muted-foreground">
            {openGroups > 0
              ? t('rebookAgain.descriptionCount', { count: openGroups })
              : t('rebookAgain.description')}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <label className="grid gap-1">
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('rebookAgain.quality')}
              </span>
              <select
                className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                value={quality}
                onChange={(e) => setQuality(e.target.value as '1' | '2' | '3')}
              >
                <option value="1">{t('rebookAgain.quality1')}</option>
                <option value="2">{t('rebookAgain.quality2')}</option>
                <option value="3">{t('rebookAgain.quality3')}</option>
              </select>
            </label>
            <label className="grid gap-1">
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('rebookAgain.cap')}
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                placeholder={t('rebookAgain.capPlaceholder')}
                className={cn(
                  'h-8 rounded-md border border-border bg-background px-2 text-xs',
                  !capValid && 'border-destructive',
                )}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-[11.5px]">
            <input
              type="checkbox"
              checked={acceptTop}
              onChange={(e) => setAcceptTop(e.target.checked)}
            />
            {t('rebookAgain.acceptTop')}
          </label>
          <label className="flex items-center gap-2 text-[11.5px]">
            <input
              type="checkbox"
              checked={regenerate}
              onChange={(e) => setRegenerate(e.target.checked)}
            />
            {t('rebookAgain.regenerate')}
          </label>
          {preview ? (
            <p
              className="rounded-md border border-dashed border-border px-2 py-1.5 text-[11px] text-muted-foreground"
              data-testid="tower-rebook-again-preview"
            >
              <b className="font-semibold text-foreground">{t('rebookAgain.dryRun')}</b>{' '}
              {t('rebookAgain.wouldAccept', { count: preview.wouldAccept })}
              {preview.skippedByCap
                ? t('rebookAgain.skippedByCap', { count: preview.skippedByCap })
                : ''}{' '}
              · {formatPriceMinor(preview.wouldPriceMinor, 'USD', t)} ·{' '}
              {t('rebookAgain.rank', { rank: preview.qualityRank })} ·{' '}
              {t('rebookAgain.nothingChanged')}
            </p>
          ) : null}
          {trigger.isError ? (
            <p className="text-[11px] text-destructive">{t('console.runError')}</p>
          ) : null}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={simulate}
              disabled={trigger.isPending || !capValid}
              data-testid="tower-rebook-again-simulate"
            >
              {trigger.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : null}
              {t('rebookAgain.simulate')}
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs"
              onClick={run}
              disabled={trigger.isPending || !capValid}
              data-testid="tower-rebook-again-run"
            >
              <Play className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('rebookAgain.run')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
